import { PHOTOS } from "@/assets/photos";
import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";

import { PageHero, Section } from "@/components/site/section";
import { StatTile } from "@/components/site/cards";
import { squadQuery } from "@/lib/site-queries";

export const Route = createFileRoute("/national-team/statistics")({
  head: () => ({
    meta: [
      { title: "National Team Statistics | Argentinian.Soccer" },
      { name: "description", content: "Argentina National Team statistics: FIFA Ranking, most caps and most goals." },
      { property: "og:title", content: "National Team Statistics | Argentinian.Soccer" },
      { property: "og:url", content: "/national-team/statistics" },
    ],
    links: [{ rel: "canonical", href: "/national-team/statistics" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(squadQuery("senior")),
  component: StatsPage,
});

function StatsPage() {
  const { data: squad } = useSuspenseQuery(squadQuery("senior"));
  const mostCaps = [...squad].sort((a, b) => (b.caps ?? 0) - (a.caps ?? 0)).slice(0, 10);
  const mostGoals = [...squad].sort((a, b) => (b.goals ?? 0) - (a.goals ?? 0)).slice(0, 10);

  return (
    <>
      <PageHero eyebrow="National Team" title="Argentina National Team Statistics" image={PHOTOS.teamHuddle} />

      <Section id="fifa-ranking" eyebrow="Statistics" title="Fifa Ranking">
        <div className="grid gap-4 md:grid-cols-3">
          <StatTile label="World Ranking" value="#1" sub="FIFA / Coca-Cola Men's Ranking" />
          <StatTile label="Conmebol" value="#1" sub="Reigning World Champions" />
          <StatTile label="Rating Points" value="1881" sub="Updated monthly" />
        </div>
      </Section>

      <StatTable id="most-caps" title="Most Caps" rows={mostCaps} valueKey="caps" valueLabel="Caps" />
      <StatTable id="most-goals" title="Most Goals" rows={mostGoals} valueKey="goals" valueLabel="Goals" />
    </>
  );
}

type Row = { id: string; name: string; club: string | null; caps: number | null; goals: number | null };

function StatTable({ id, title, rows, valueKey, valueLabel }: {
  id: string; title: string; rows: Row[]; valueKey: "caps" | "goals"; valueLabel: string;
}) {
  return (
    <Section id={id} eyebrow="Statistics" title={title}>
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left">
            <tr>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Player</th>
              <th className="px-4 py-3">Club</th>
              <th className="px-4 py-3 text-right">{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-t border-border">
                <td className="px-4 py-3 font-bold text-primary">{i + 1}</td>
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.club}</td>
                <td className="px-4 py-3 text-right font-bold">{r[valueKey] ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
